import type { components } from '@fluencelabs/vodopad-admin-api'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'

import client from '@/api/client'
import { unwrapApiData, unwrapApiResult } from '@/api/unwrapApiResult'
import { invalidateAdminCaches } from '@/utils/cacheInvalidation'

import { mapGrantPermissionError } from '../grantError'

type PermissionScope = components['schemas']['PermissionScope']

// Runtime scopes can lead the generated union during rolling API upgrades.
const toPermissionScopes = (permissions: string[]) =>
  permissions as PermissionScope[]

export function useRoleActions() {
  const queryClient = useQueryClient()

  const refresh = () => invalidateAdminCaches(queryClient)

  const createRole = async (
    name: string,
    description: string | undefined,
    permissions: string[],
  ) => {
    try {
      const result = await client.POST('/v1/admin/user_roles', {
        body: {
          name,
          description: description || null,
          permissions: toPermissionScopes(permissions),
        },
      })
      unwrapApiData(result, 'Failed to create role')
    } catch (error) {
      throw mapGrantPermissionError(error)
    }
    void message.success('Role created')
    refresh()
  }

  const updateRoleDescription = async (
    roleName: string,
    description: string,
  ) => {
    const result = await client.PATCH('/v1/admin/user_roles/{role_name}', {
      params: { path: { role_name: roleName } },
      body: { description },
    })
    unwrapApiData(result, 'Failed to update role')
    void message.success('Role updated')
    refresh()
  }

  const setRolePermissions = async (
    roleName: string,
    permissions: string[],
  ) => {
    try {
      const result = await client.PATCH(
        '/v1/admin/user_roles/{role_name}/permissions',
        {
          params: { path: { role_name: roleName } },
          body: { permissions: toPermissionScopes(permissions) },
        },
      )
      unwrapApiData(result, 'Failed to update permissions')
    } catch (error) {
      throw mapGrantPermissionError(error)
    }
    void message.success('Permissions updated')
    refresh()
  }

  const deleteRole = async (roleName: string) => {
    const result = await client.DELETE('/v1/admin/user_roles/{role_name}', {
      params: { path: { role_name: roleName } },
    })
    unwrapApiResult(result, 'Failed to delete role')
    void message.success('Role deleted')
    refresh()
  }

  return { createRole, updateRoleDescription, setRolePermissions, deleteRole }
}
